import { storeToRefs } from 'pinia'
import { nanoid } from 'nanoid'
import { useSlidesStore, useMainStore, useMasterSlidesStore } from '@/store'
import { PPTElement, Slide } from '@/types/slides'
import { createSlideIdMap, createElementIdMap } from '@/utils/element'
import useHistorySnapshot from '@/hooks/useHistorySnapshot'

export default () => {
  const mainStore = useMainStore()
  const slidesStore = useSlidesStore()
  const masterSlidesStore = useMasterSlidesStore()
  const { currentSlide } = storeToRefs(slidesStore)

  const { addHistorySnapshot } = useHistorySnapshot()

  /**
   * Add the specified element data (a set)
   * @param elements element list data
   */
  const addElementsFromData = (elements: PPTElement[]) => {
    const { groupIdMap, elIdMap } = createElementIdMap(elements)
    const currentSlideElementIdList = currentSlide.value.elements.map(el => el.id)

    for (const element of elements) {
      const inCurrentSlide = currentSlideElementIdList.includes(element.id)

      element.id = elIdMap[element.id]

      if (element.type === 'section') {
        element.slideLayoutId = elIdMap[element.slideLayoutId]
      }

      if (element.type === 'slidelayout') {
        for (const section of element.layout.children) {
          section.id = nanoid(10)
          for (const subsection of section.children) {
            subsection.id = elIdMap[subsection.id]
          }
        }
      }

      if (element.type === 'dragdrop') {
        for (const row  of element.layout) {

          elIdMap[row.id] = nanoid(10)
          row.id = elIdMap[row.id]

          for (const col of row.children) {
            elIdMap[col.id] = nanoid(10)
            col.id = elIdMap[col.id]
          }
        }

        for (const widget of element.widgets) {
          const first = widget.parent.substring(0, 10)
          const second = widget.parent.substring(11, 21)
          widget.parent = elIdMap[first] + '-' + elIdMap[second]

          for (const child of widget.children) {
            child.id = nanoid(10)
          }
        }
      }

      if (inCurrentSlide) {
        element.left = element.left + 10
        element.top = element.top + 10
      }

      if (element.groupId) element.groupId = groupIdMap[element.groupId]
    }
    slidesStore.addElement(elements)
    mainStore.setActiveElementIdList(Object.values(elIdMap))
    addHistorySnapshot()
  }

  /**
   * Add specified page data
   * @param slide page data
   */
  const addSlidesFromData = (slides: Slide[], masterSlideId = null) => {
    const slideIdMap = createSlideIdMap(slides)
    const newSlides = slides.map(slide => {
      const { groupIdMap, elIdMap } = createElementIdMap(slide.elements)

      const sectionMap = {}
      const subsectionMap = {}
      const layoutIdMap = {}

      const previousMasterSlideId = currentSlide.value.masterSlideId
      if (previousMasterSlideId && previousMasterSlideId !== masterSlideId) {
        const masterSlide = masterSlidesStore.masterSlides.find(slide => slide.masterSlideId === previousMasterSlideId)
        if (masterSlide) {
          const slideLayout = currentSlide.value.elements.find(element => element.type === 'slidelayout')
          removeHeaderAndFooter(slideLayout.layout.children)
        }
      }

      if (masterSlideId) slide.masterSlideId = masterSlideId

      for (const element of slide.elements) {

        element.id = elIdMap[element.id]

        // TODO this is duplicate code that needs to be moved to a common function

        if (element.type === 'section') {
          element.slideLayoutId = elIdMap[element.slideLayoutId]
        }

        if (element.type === 'slidelayout') {
          for (const section of element.layout.children) {
            section.id = nanoid(10)
            for (const subsection of section.children) {
              subsection.id = elIdMap[subsection.id]
            }
          }
        }

        if (element.type === 'dragdrop') {
          for (const row  of element.layout) {
            elIdMap[row.id] = nanoid(10)
            row.id = elIdMap[row.id]

            for (const col of row.children) {
              elIdMap[col.id] = nanoid(10)
              col.id = elIdMap[col.id]
            }
          }

          for (const widget of element.widgets) {
            const first = widget.parent.substring(0, 10)
            const second = widget.parent.substring(11, 21)
            widget.parent = elIdMap[first] + '-' + elIdMap[second]

            for (const child of widget.children) {
              if (child) {
                child.id = nanoid(10)
              }
            }
          }
        }


      }
      // animation id replacement
      if (slide.animations) {
        for (const animation of slide.animations) {
          animation.id = nanoid(10)
          animation.elId = elIdMap[animation.elId]
        }
      }

      return {
        ...slide,
        id: slideIdMap[slide.id],
      }
    })
    return newSlides
  }

  const removeHeaderAndFooter = (arr) => {
    return arr.filter(child => child.subtype !== 'header' && child.subtype !== 'footer')
  }

  return {
    addElementsFromData,
    addSlidesFromData,
  }
}
